/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { IconButton, Tooltip as MuiTooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import CopyButton from './CopyButton';
import { eventLabel, eventDefinition } from './eventTypes';

const TYPE_LABELS = {
  wms: 'WMS', cart: 'Cart', carrier: 'Carrier', freight: 'Freight', erp: 'ERP',
};

function severityChipClass(severity) {
  if (severity === 'error') return 'badge-error';
  if (severity === 'warning') return 'badge-warning';
  return 'badge-neutral';
}

function severityDotClass(severity) {
  if (severity === 'error') return 'status-dot-error';
  if (severity === 'warning') return 'status-dot-warning';
  return 'status-dot-neutral';
}

function formatTimestamp(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

// flat editorial section: an uppercase muted header, hairline divider below the
// whole block; no fill or rounded box

// one label/value line in the details list, separated by a thin hairline; mono
// value on the right, optional copy affordance
function DetailRow({
  label, value, mono = false, copyable = false,
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-sm text-surface-500 shrink-0">{label}</span>
      <span className="flex items-center gap-1 min-w-0 justify-end">
        <span className={`text-surface-800 text-right break-all ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value}</span>
        {copyable && value && value !== '—' && <CopyButton value={value} iconSize="12px" />}
      </span>
    </div>
  );
}

// what-happened description per event type
function whatHappened(event) {
  const name = event.displayName || event.integration_name;
  switch (event.type) {
    case 'install_failed':
      return `An end-user tried to connect ${name} and credential validation failed. No connection was created.`;
    case 'connection_error':
      return `${name} reported an error on this connection.`;
    case 'connection_created':
      return `A ${name} connection was created.`;
    case 'connection_reinstalled':
      return `An existing ${name} connection was reconnected with new credentials.`;
    case 'connection_deleted':
      return `A ${name} connection was deleted.`;
    case 'connection_disabled':
      return `Every schedule on this ${name} connection was paused, so it stopped syncing.`;
    case 'connection_enabled':
      return `This ${name} connection started syncing again after being fully paused.`;
    case 'schedule_stuck':
      return `A ${name} recurring sync stopped making progress. Trackstar investigates automatically and it resumes once the issue is resolved.`;
    case 'schedule_unstuck':
      return `A stuck ${name} schedule started making progress again.`;
    case 'schedule_paused':
      return `A ${name} recurring sync was paused.`;
    case 'schedule_unpaused':
      return `A ${name} recurring sync was resumed.`;
    case 'historical_sync_completed':
      return `The first (historical) ${name} sync finished.`;
    case 'on_demand_sync_stuck':
      return `An on-demand ${name} sync stopped making progress and won't resume on its own.`;
    case 'on_demand_sync_started':
      return `An on-demand ${name} sync started.`;
    case 'on_demand_sync_completed':
      return `An on-demand ${name} sync completed.`;
    case 'on_demand_sync_cancelled':
      return `An on-demand ${name} sync was cancelled.`;
    default:
      return event.message;
  }
}

function EventDetailDrawer({ event, closeDrawer }) {
  if (!event) return null;

  const typeLabel = TYPE_LABELS[event.integration_type] || event.integration_type;
  let logo = `https://trackstarlogosbucket.s3.amazonaws.com/${event.integration_name}.png`;
  if (event.integration_name === 'sandbox') logo = 'ts_logo_2024.png';
  const meta = event.additional_fields || {};

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-surface-0/90 backdrop-blur-md border-b border-surface-200 px-6 py-3">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-surface-500">Event Details</span>
          <IconButton onClick={closeDrawer} size="small" className="hover:bg-surface-100" sx={{ padding: '4px' }}>
            <CloseIcon sx={{ fontSize: '18px', color: '#78716C' }} />
          </IconButton>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6" style={{ scrollbarGutter: 'stable' }}>
        {/* Header: integration + severity, with the key ids as header metadata */}
        <div className="py-5 border-b border-surface-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 shrink-0 rounded-md bg-surface-100 flex items-center justify-center border border-surface-200">
              <img src={logo} alt={`${event.integration_name} logo`} className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-surface-800 capitalize">{event.integration_name}</p>
                <span className="text-xs text-surface-400">{typeLabel}</span>
                <MuiTooltip title={eventDefinition(event.type)} arrow placement="top">
                  <span className={`badge ${severityChipClass(event.severity)} shrink-0 cursor-help`}>
                    <span className={`status-dot ${severityDotClass(event.severity)}`} />
                    {eventLabel(event.type)}
                  </span>
                </MuiTooltip>
              </div>
              <p className="text-xs text-surface-500 mt-1">{formatTimestamp(event.created_at)}</p>
            </div>
          </div>

          {(event.connection_id || event.customer_id) && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 mt-4 text-xs">
              {event.connection_id && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-surface-400 shrink-0">Connection ID</span>
                  <Link to={`/connections?id=${event.connection_id}`} className="font-mono text-surface-600 hover:underline truncate">
                    {event.connection_id}
                  </Link>
                  <CopyButton value={event.connection_id} label="Copy Connection ID" iconSize="12px" />
                </div>
              )}
              {event.customer_id && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-surface-400 shrink-0">Customer ID</span>
                  <span className="font-mono text-surface-600 truncate">{event.customer_id}</span>
                  <CopyButton value={event.customer_id} label="Copy Customer ID" iconSize="12px" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-surface-700 leading-relaxed pt-5">
          {whatHappened(event)}
        </p>

        {/* Structured facts, grouped in one subtle panel */}
        {(meta.function_name || meta.link_token) && (
          <div className="mt-4 rounded-lg border border-surface-200 bg-surface-50 px-4 py-2.5">
            {meta.function_name && <DetailRow label="Function" value={meta.function_name} mono />}
            {meta.requested_start_time && (
              <DetailRow label="Requested start" value={formatTimestamp(meta.requested_start_time)} />
            )}
            {meta.requested_end_time && (
              <DetailRow label="Requested end" value={formatTimestamp(meta.requested_end_time)} />
            )}
            {meta.link_token && <DetailRow label="Link token" value={meta.link_token} mono copyable />}
          </div>
        )}

        {/* Error message — only where it is the real error, not a restatement */}
        {(event.type === 'install_failed' || event.type === 'connection_error') && event.message && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-surface-500 mb-2">
              {event.type === 'install_failed' ? 'Error message' : 'Details'}
            </p>
            <p className="pl-3 border-l-2 border-error text-surface-700 font-mono text-xs break-words whitespace-pre-wrap leading-relaxed">
              {event.message}
            </p>
          </div>
        )}

        {/* Affected endpoints (connection_error), grouped in one panel */}
        {event.type === 'connection_error' && Array.isArray(meta.affected_endpoints)
          && meta.affected_endpoints.length > 0 && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-surface-500 mb-2">Affected endpoints</p>
            <div className="rounded-lg border border-surface-200 bg-surface-50 divide-y divide-surface-200">
              {meta.affected_endpoints.map((ep) => (
                <div key={ep.endpoint} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="text-sm font-mono text-surface-700 truncate">{ep.endpoint}</span>
                  <span className={`text-xs shrink-0 ${ep.preventing_sync ? 'text-error-dark' : 'text-surface-400'}`}>
                    {ep.preventing_sync ? 'Blocking' : 'Non-blocking'}
                  </span>
                </div>
              ))}
            </div>
            {event.connection_id && (
              <Link
                to={`/api-log?connection=${event.connection_id}`}
                className="inline-block mt-3 text-sm text-accent-600 hover:underline"
              >
                View API logs
              </Link>
            )}
          </div>
        )}
        <div className="pb-6" />
      </div>
    </div>
  );
}

export default EventDetailDrawer;
